import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AuthState, AuthContextType, AuthUser } from './auth/types';
import {
  createPermissionsMap,
  hasPermission as checkPermission,
  mergePermissionsFromDb,
  isRafaelUser,
  normalizeEmail,
} from './auth/permissions';
import { getStoredAuth, storeAuth, clearAuth } from './auth/storage';
import { authenticateUser } from './auth/api';
import { authenticateLocalUser } from './auth/localAuth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Restaura sessão do localStorage antes do 1º paint — menu e permissões aparecem sem esperar o Supabase. */
function readInitialAuthState(): AuthState {
  const { user, token } = getStoredAuth();
  if (user?.id && token) {
    return {
      user: { ...user, token },
      isAuthenticated: true,
      isLoading: false,
    };
  }
  return { user: null, isAuthenticated: false, isLoading: false };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(readInitialAuthState);

  useEffect(() => {
    let cancelled = false;

    const refreshAuthFromDb = async () => {
      const { user, token } = getStoredAuth();
      if (!user?.id || !token) {
        return;
      }

      try {
        const { data: usuario, error: uErr } = await supabase
          .from('usuarios')
          .select('id, nome, email, perfil_padrao, origem, ativo')
          .eq('id', user.id)
          .single();

        if (uErr || !usuario) {
          return;
        }

        // Usuário local desativado perde a sessão já aberta
        if (usuario.origem === 'local' && !usuario.ativo) {
          clearAuth();
          if (!cancelled) {
            setAuthState({ user: null, isAuthenticated: false, isLoading: false });
          }
          return;
        }

        const { data: permRows, error: pErr } = await supabase
          .from('usuarios_permissoes')
          .select('pagina, acesso')
          .eq('usuario_id', usuario.id);

        if (pErr) {
          console.error('Erro ao atualizar permissões da sessão:', pErr);
          return;
        }

        const permissoesMap = mergePermissionsFromDb(
          {
            id: usuario.id,
            email: usuario.email ?? '',
            perfil_padrao: usuario.perfil_padrao,
          },
          permRows
        );

        const authUser = {
          id: usuario.id,
          nome: usuario.nome,
          email: normalizeEmail(usuario.email) || (usuario.email ?? ''),
          token,
          permissoes: permissoesMap,
          perfil_padrao: usuario.perfil_padrao,
          origem: usuario.origem === 'local' ? ('local' as const) : ('externo' as const),
        };

        storeAuth(authUser);
        if (!cancelled) {
          setAuthState({
            user: authUser,
            isAuthenticated: true,
            isLoading: false,
          });
        }
      } catch (e) {
        console.error('Falha ao sincronizar permissões:', e);
      }
    };

    refreshAuthFromDb();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      console.log('🔑 Tentando autenticar usuário:', email);

      // Buscar usuário na tabela local (case-insensitive; ilike trata "_" como curinga, por isso o filtro exato)
      const emailNorm = email.trim().toLowerCase();
      const { data: candidatos, error: userError } = await supabase
        .from('usuarios')
        .select('*')
        .ilike('email', emailNorm);

      const usuario = (candidatos ?? []).find(
        (u: { email: string | null }) => (u.email ?? '').trim().toLowerCase() === emailNorm
      );

      if (userError) {
        console.error('❌ Erro ao buscar usuário na base local:', userError);
        return { success: false, message: 'Erro interno do sistema. Tente novamente.' };
      }

      let token: string;

      if (usuario?.origem === 'local') {
        // Usuário próprio do portal: senha validada no Supabase Auth
        if (!usuario.ativo) {
          return { success: false, message: 'Usuário inativo. Procure o administrador.' };
        }
        const localToken = await authenticateLocalUser(emailNorm, password);
        if (!localToken) {
          return { success: false, message: 'Usuário ou senha inválidos.' };
        }
        token = localToken;
      } else {
        // Usuário do portal externo: senha validada na API da Prospera
        const authData = await authenticateUser(email, password);
        console.log('📥 Resposta da API:', authData);

        if (!authData.data?.token) {
          console.error('❌ Token não encontrado na resposta');
          return { success: false, message: 'Usuário ou senha inválidos.' };
        }
        if (!usuario) {
          console.error('❌ Usuário não encontrado na base local');
          return { success: false, message: 'Usuário não cadastrado no sistema.' };
        }
        token = authData.data.token;
      }

      // Permissões: merge com todas as páginas (restrito) ou mapa admin + sobrescritas do DB
      let permissoesMap = createPermissionsMap(usuario);

      if (!isRafaelUser({ id: usuario.id, email: usuario.email })) {
        const { data: permissoes, error: permError } = await supabase
          .from('usuarios_permissoes')
          .select('pagina, acesso')
          .eq('usuario_id', usuario.id);

        if (permError) {
          console.error('❌ Erro ao buscar permissões:', permError);
          return { success: false, message: 'Erro ao carregar permissões do usuário.' };
        }

        permissoesMap = mergePermissionsFromDb(
          {
            id: usuario.id,
            email: usuario.email ?? '',
            perfil_padrao: usuario.perfil_padrao,
          },
          permissoes
        );

        console.log('✅ Permissões carregadas para:', usuario.perfil_padrao);
        console.log('📋 Permissões do usuário:', permissoesMap);
      }

      const authUser: AuthUser = {
        id: usuario.id,
        nome: usuario.nome,
        email: normalizeEmail(usuario.email) || (usuario.email ?? ''),
        token,
        permissoes: permissoesMap,
        perfil_padrao: usuario.perfil_padrao,
        origem: usuario.origem === 'local' ? 'local' : 'externo'
      };

      console.log('👤 AuthUser criado:', {
        nome: authUser.nome,
        perfil: authUser.perfil_padrao,
        permissoes: authUser.permissoes
      });

      // Armazenar dados da sessão
      storeAuth(authUser);

      setAuthState({
        user: authUser,
        isAuthenticated: true,
        isLoading: false
      });

      console.log('✅ Login realizado com sucesso');
      return { success: true };

    } catch (error) {
      console.error('💥 Erro no processo de login:', error);
      
      if (error instanceof Error && error.message.includes('status: 4')) {
        return { success: false, message: 'Usuário ou senha inválidos.' };
      }
      
      return { success: false, message: 'Erro interno do sistema. Tente novamente.' };
    }
  };

  const logout = () => {
    console.log('🚪 Fazendo logout...');
    clearAuth();
    
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false
    });
  };

  const hasPermission = (pagina: string): boolean => {
    return checkPermission(authState.user, pagina);
  };

  return (
    <AuthContext.Provider value={{
      ...authState,
      login,
      logout,
      hasPermission
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
